import React, {MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Screen from "../components/Screen";
import Box, {Direction} from "../components/Box";
import TappableAvatar from "../components/TappableAvatar";
import {rem} from "../utils/CSSUtils";
import Title from "../components/Title";
import {IGroupStore} from '../stores/GroupStore';
import {observer} from "mobx-react";
import {toI18N} from "../utils/I18n";
import {IGroup} from "../stores/models";
import {NavigationProps} from "../constants/Props";
import {showErrorToast, showInfoToast, usePrevious} from "../utils/HooksHelper";
import isUrl from "is-url";
import Modals, {ModalsRefProps} from "../components/Modals";
import Alert, {AlertRefProps} from "../components/Alert";
import {GlobalContext} from "../constants/Providers";
import Button from "../components/Button";
import MultiGroupSelector from "../components/MultiGroupSelector";
import styles from "../css/Text.module.css";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    groups: IGroup[]
    store: IGroupStore,
    markDeeplinkAsExpired: Function,
    setConfirmModal: (arg: { show: boolean, message?: string, onConfirm?: (e: MouseEvent) => void }) => void,
}

function SelectGroupScreen(props: Props) {

    //Props

    const {
        onBack,
        onNext,
        groups,
        setConfirmModal,
        markDeeplinkAsExpired,
        store: {
            id,
            setId,
            setName,
            setURLAvatar,
            removeAvatar,
            setExtraGroupIds,
            extraGroupIds,
            canProceedForSelectingGroup,
            avatar,
            fetchingDeeplink,
            setFetchingDeeplink,
            setIsOwner
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_select_group_page);
        }
    });


    const {editingDeeplinkUrl} = useContext(GlobalContext);

    // Memo

    const options = useMemo(() => {
        return groups.map(({id, name, selectId}, index) => {
            return {id, selectId, name, value: `${index}`, label: `${name} ${selectId}`};
        });
    }, [groups]);

    // State

    const [defaultGroup, setDefaultGroup] = useState<{ value: string, label: string, fixed?: boolean, onClick?: Function } | undefined>(undefined);

    // Refs

    const modalsRef = useRef<ModalsRefProps>(null);
    const alertRef = useRef<AlertRefProps>(null);

    // Callbacks

    const expireEditedLink = useCallback(() => {
        const handle = async () => {
            await markDeeplinkAsExpired();
            showInfoToast(alertRef, toI18N('select_group_link_marked_as_expired'));
        }
        setConfirmModal({
            show: true,
            message: toI18N("are_you_sure_you_want_to_expired"),
            onConfirm: async () => {
                await handle();
                setConfirmModal({show: false});
            }
        });
    }, [alertRef, markDeeplinkAsExpired, setConfirmModal])

    const selectGroup = useCallback(
        (indexes) => {
            if (!indexes || !indexes.length) {

                setId('');
                setName('');
                removeAvatar();
                setDefaultGroup(undefined);
                setIsOwner(false);
                setExtraGroupIds([]);

            } else {

                const mainGroupIndex = indexes[0];

                if (id !== groups[mainGroupIndex].id) {
                    setId(groups[mainGroupIndex].id);
                    setName(groups[mainGroupIndex].name);
                    setIsOwner(groups[mainGroupIndex].isOwner);
                    setDefaultGroup(options[mainGroupIndex]);
                    setURLAvatar(groups[mainGroupIndex].avatar, true);
                }

                setExtraGroupIds(indexes.slice(1).map((v: number) => groups[v].id));

            }

        },
        [groups, options, setExtraGroupIds, setName, removeAvatar, setDefaultGroup, setURLAvatar, setId, id, setIsOwner],
    );

    const onTryingToRemoveMainGroup = useCallback(() => {
        showInfoToast(alertRef, toI18N('select_group_cannot_remove_main_group'));
    }, [alertRef]);


    // Effects

    useEffect(() => {
        if (!isActive) { return ; }
        if (groups.length && !extraGroupIds.length) {
            let index: number;
            if (id) {
                index = groups.findIndex(group => group.id === id);
                index = index !== -1 ? index : 0;
                selectGroup([index]);
                setDefaultGroup(options[index]);
                return
            }
            // Using 'cloudfront' as a hack to know if it is not the default avatar
            index = groups.findIndex(group => group.avatar.indexOf('cloudfront') !== -1);
            index = index !== -1 ? index : 0;
            selectGroup([index]);
            setDefaultGroup(options[index]);
        } else if (id) {
            setDefaultGroup(options[groups.findIndex(group => group.id === id)]);
        }
        // eslint-disable-next-line
    }, [isActive]); // mimic component did mount

    const value: any = extraGroupIds.map((groupId) => {
        return options.find(({id}) => id === groupId);
    });

    value.unshift(defaultGroup);

    if (editingDeeplinkUrl && defaultGroup) {
        defaultGroup.fixed = true;
    }
    if (!editingDeeplinkUrl && defaultGroup) {
        defaultGroup.fixed = false;
    }

    return (
        <Screen
            title={"select_group_screen_header_title"}
            subtitle={"select_group_screen_header_subtitle"}
            onBack={onBack}
            disabledNext={!canProceedForSelectingGroup}
            onNext={onNext}>

            <Alert ref={alertRef}/>

            <Modals
                id={'select-group-screen'}
                modalTextInputValidation={isUrl}
                showLoadingIndicator={fetchingDeeplink}
                ref={modalsRef}/>

            <Box center style={{marginTop: rem(116)}}>
                <Title shrink title={toI18N("select_group_title")}/>
            </Box>

            <Box grow center sideMargin direction={Direction.Column}>

                <TappableAvatar src={avatar ? avatar.path : ''}
                                onLoad={avatar ? avatar.setIsLoaded : undefined}
                />

                <Box direction={Direction.Clear} topMargin={28}>
                    <MultiGroupSelector
                        id={'select-group-multi-group-selector'}
                        onTryingToRemoveFixedOption={onTryingToRemoveMainGroup}
                        placeholder={toI18N("select_group_select_placeholder")}
                        value={value}
                        options={options}
                        label={toI18N("select_group_select_groups_owner_or_admin")}
                        hint={toI18N("select_group_select_hint")}
                        onSelectGroup={selectGroup}/>
                </Box>

            </Box>

            {editingDeeplinkUrl ? <Box sideMargin bottomMargin={44} center>
                    <Button minWidth={250} onClick={expireEditedLink} alert>
                        {toI18N('select_group_expire_edited_link')}
                    </Button>
                    <label htmlFor={id} className={styles.hint}>{toI18N("select_group_expire_edited_link_hint")}</label>
                </Box>
                : null}

        </Screen>
    );
}

export default observer(SelectGroupScreen);

